import type {
  AllHTMLAttributes,
  ButtonHTMLAttributes,
  DOMAttributes,
  LinkHTMLAttributes,
  Ref,
} from 'react';
import { forwardRef } from 'react';

import type { MarginProps } from '@/components/Box';
import type { CountryFlagIconProps } from '@/components/CountryFlagIcon';
import { CountryFlagIcon } from '@/components/CountryFlagIcon';
import type { IconProps } from '@/components/Icon/Icon';
import { Icon } from '@/components/Icon/Icon';
import type { IconSize } from '@/components/Icon/Icon.types';

import type { RequireOnlyOne } from '../../utils/tsutils';

import * as S from './IconButton.styles';
import { defaultProps } from './IconButton.styles';

export type IconButtonVariant = 'filled' | 'outlined' | 'ghosted';
export type IconButtonColorVariant =
  | 'default'
  | 'defaultInverted'
  | 'primary'
  | 'destructive';
export type IconButtonSize = 's' | 'm' | 'l';

export interface IconButtonPartialProps
  extends Pick<
      ButtonHTMLAttributes<HTMLElement>,
      | 'autoFocus'
      | 'disabled'
      | 'form'
      | 'formAction'
      | 'formEncType'
      | 'formMethod'
      | 'formNoValidate'
      | 'formTarget'
      | 'name'
      | 'type'
      | 'value'
    >,
    Pick<DOMAttributes<HTMLElement>, 'onClick' | 'onFocus' | 'onBlur'>,
    MarginProps,
    Pick<LinkHTMLAttributes<HTMLElement>, 'href' | 'rel'>,
    Pick<AllHTMLAttributes<HTMLElement>, 'target'> {
  as?: Extract<keyof JSX.IntrinsicElements, 'span' | 'button' | 'a'>;
  colorVariant?: IconButtonColorVariant;
  countryFlagIconProps: Omit<CountryFlagIconProps, 'size'>;
  iconProps: Pick<IconProps, 'variant' | 'name'> & { size?: IconSize };
  size?: IconButtonSize;
  variant?: IconButtonVariant;
}

export type IconButtonProps = RequireOnlyOne<
  IconButtonPartialProps,
  'countryFlagIconProps' | 'iconProps'
>;

export const iconSize: Record<IconButtonSize, IconSize> = {
  s: 'm',
  m: 'l',
  l: 'l',
};

export const IconButton = forwardRef(
  (props: IconButtonProps, ref: Ref<HTMLElement>) => {
    const { as, countryFlagIconProps, iconProps, size = 'm' } = props;

    return (
      <S.IconButtonContainer
        ref={ref}
        forwardedAs={as}
        {...props}
        data-test-id='IconButtonContainer'
      >
        {iconProps && (
          <Icon
            {...iconProps}
            data-test-id='IconButtonIcon'
            size={iconProps.size || iconSize[size]}
          />
        )}
        {countryFlagIconProps && (
          <CountryFlagIcon
            {...countryFlagIconProps}
            data-test-id='IconButtonCountryFlagIcon'
            size={iconSize[size]}
          />
        )}
      </S.IconButtonContainer>
    );
  },
);

IconButton.defaultProps = defaultProps;

IconButton.displayName = 'IconButton';
